/**
 * useMentionPrompt Hook
 *
 * State management and orchestration hook for MentionPromptField.
 * Connects the UI to the MentionHydrationService for mention resolution.
 *
 * Responsibilities:
 * - Manage value state (HTML content)
 * - Manage preview mode (expression vs result)
 * - Orchestrate hydration processing
 * - Provide processed content for preview
 * - Notify parent of hydrated results
 *
 * Design Decisions:
 * - Dependency injection of hydrationService for testability
 * - Debounced processing to handle rapid changes
 * - Graceful error handling for processing failures
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  IMentionHydrationService,
  MentionDataContext,
  UseMentionPromptOptions,
  UseMentionPromptResult,
  MentionEditorProps,
} from './types';
import { MentionHydrationService } from './MentionHydrationService';

// Default singleton instance
const defaultHydrationService = new MentionHydrationService();

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 150;

/**
 * useMentionPrompt Hook
 *
 * @param options - Hook configuration options
 * @returns Hook result with state, actions, and computed values
 */
export function useMentionPrompt(options: UseMentionPromptOptions): UseMentionPromptResult {
  const {
    initialValue = '',
    dataContext,
    onHydratedResultChange,
    hydrationService = defaultHydrationService,
  } = options;

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [value, setValueInternal] = useState<string>(initialValue);
  const [previewMode, setPreviewModeInternal] = useState<'expression' | 'result'>('expression');
  const [previewContent, setPreviewContent] = useState<string>('');
  const [hydratedResult, setHydratedResult] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Refs for debouncing and tracking latest values
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef<string>(value);
  const latestContextRef = useRef<MentionDataContext>(dataContext);

  // Update refs when values change
  latestValueRef.current = value;
  latestContextRef.current = dataContext;

  // ===========================================================================
  // PROCESSING LOGIC
  // ===========================================================================

  /**
   * Process content for preview and hydration
   */
  const processContent = useCallback(
    (html: string, context: MentionDataContext, mode: 'expression' | 'result') => {
      try {
        setIsProcessing(true);

        // Process for preview based on mode
        const preview =
          mode === 'expression'
            ? hydrationService.processForExpression(html, context)
            : hydrationService.processForResult(html, context);

        setPreviewContent(preview);

        // Always compute hydrated result for AI submission
        const result = hydrationService.processForResult(html, context);
        setHydratedResult(result);

        // Notify parent if callback provided
        if (onHydratedResultChange) {
          onHydratedResultChange(result);
        }
      } catch (error) {
        // Graceful error handling - log but don't crash
        console.warn('MentionPrompt: Error processing content', error);
        setPreviewContent(html);
        setHydratedResult(html);
      } finally {
        setIsProcessing(false);
      }
    },
    [hydrationService, onHydratedResultChange]
  );

  /**
   * Debounced processing to handle rapid changes
   */
  const debouncedProcess = useCallback(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced timer
    debounceTimerRef.current = setTimeout(() => {
      processContent(
        latestValueRef.current,
        latestContextRef.current,
        previewMode
      );
    }, DEBOUNCE_DELAY);
  }, [processContent, previewMode]);

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Set value and trigger debounced processing
   */
  const setValue = useCallback(
    (newValue: string) => {
      setValueInternal(newValue);
      latestValueRef.current = newValue;
      debouncedProcess();
    },
    [debouncedProcess]
  );

  /**
   * Set preview mode and immediately reprocess
   */
  const setPreviewMode = useCallback(
    (mode: 'expression' | 'result') => {
      setPreviewModeInternal(mode);
      // Process immediately with new mode (no debounce)
      processContent(latestValueRef.current, latestContextRef.current, mode);
    },
    [processContent]
  );

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Initial processing on mount
   */
  useEffect(() => {
    if (initialValue) {
      processContent(initialValue, dataContext, previewMode);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Reprocess when dataContext changes
   */
  useEffect(() => {
    processContent(value, dataContext, previewMode);
    // Intentionally only depend on dataContext to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext]);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // EDITOR PROPS
  // ===========================================================================

  /**
   * Props to spread onto RichMentionEditorWrapper
   */
  const editorProps: MentionEditorProps = useMemo(
    () => ({
      value,
      onChange: setValue,
      audiences: dataContext.audiences,
      actorsByAudience: dataContext.actorsByAudience,
      tasks: dataContext.tasks,
      studyCases: dataContext.studyCases,
    }),
    [value, setValue, dataContext]
  );

  // ===========================================================================
  // RETURN
  // ===========================================================================

  return {
    // State
    value,
    previewMode,
    previewContent,
    isProcessing,

    // Actions
    setValue,
    setPreviewMode,

    // Computed
    hydratedResult,

    // For component spread
    editorProps,
  };
}
